/**
 * /t/$slug/login/help — always-reachable sign-in assistance.
 *
 * Reachable from a footer link on every login step ("Trouble
 * signing in?") and from the QR-cancelled or HelpScreen
 * surfaces. Three sections:
 *
 *   1. Transport picker — same component used at signin-
 *      cancelled and at enrollment. Picking a card persists
 *      the preference and bounces back to /login so the
 *      next identify→ceremony uses the new transport.
 *
 *   2. "Use a different account" — clears the remembered-email
 *      hint and routes back to /login.
 *
 *   3. "Lost your passkey?" — routes to /recovery/request.
 *      Demoted (link-styled below a separator) so it's
 *      reachable but never the obvious answer.
 *
 * The page is router-driven (not a modal) so it bookmarks,
 * works with the back button, and screen-readers announce
 * it as a navigation event.
 */

import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { LoginHelpPage } from "@/features/auth/pages/login-help-page";

const loginHelpSearchSchema = z.object({
  /** Forwarded from the originating login surface so this
   *  page can pre-fill the email when bouncing back. */
  email: z.string().optional(),
  /** Same as login: optional return-to after sign-in. */
  redirect: z.string().optional(),
});

type LoginHelpSearch = z.infer<typeof loginHelpSearchSchema>;

export const Route = createFileRoute("/t/$slug/login/help")({
  validateSearch: (raw: Record<string, unknown>): LoginHelpSearch =>
    loginHelpSearchSchema.parse(raw),
  component: LoginHelpPage,
});
